import { useCallback, useEffect, useState } from 'react'

export interface ConversationPage<T> {
  conversations: T[]
  next_cursor: string | null
}

/**
 * Local copy of the server's first page of conversations plus the cursor for
 * the next one. Cable updates mutate the list in place; `appendPage` adds a
 * page loaded by infinite scroll, skipping rows the list already has (a cable
 * `new_conversation` can race a page that also contains it).
 */
export function usePaginatedConversations<T extends { id: string }>(initial: T[], initialCursor: string | null) {
  const [conversations, setConversations] = useState(initial)
  const [nextCursor, setNextCursor] = useState(initialCursor)

  // A new first page (filter change, stale refresh) replaces everything loaded so far
  useEffect(() => {
    setConversations(initial)
    setNextCursor(initialCursor)
  }, [initial, initialCursor])

  const appendPage = useCallback((page: ConversationPage<T>) => {
    setConversations(prev => {
      const seen = new Set(prev.map(c => c.id))
      return [...prev, ...page.conversations.filter(c => !seen.has(c.id))]
    })
    setNextCursor(page.next_cursor)
  }, [])

  return { conversations, setConversations, nextCursor, appendPage }
}
